import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// ReportEmailPanels.js  (2026-10)
// -----------------------------------------------------------------------------
// The monthly report e-mails, set on the page they belong to (they used to be
// in the Settings dialog):
//
//   <TimesheetEmailPanel />  — Staff clock in/out, top right
//   <ExpiredEmailPanel />    — Head Office → Date checking, top right
//   <ExportPinPanel />       — Settings → Organisation Settings (export PIN)
//
// Each is a small summary card ("Monthly timesheet email: On · …") with a
// Change button that opens the form in a drop-down beneath it, so the page
// layout doesn't move. Both read and write the signed-in organisation's
// settings through headoffice.js v55:
//   POST /api/headoffice/report-settings        → { managerEmail, timesheets, expired }
//   POST /api/headoffice/report-settings/save   { section: "timesheets" | "expired", ... }
// The group manager address itself is still set in Settings.
// -----------------------------------------------------------------------------
import React, { useCallback, useEffect, useRef, useState } from "react";
const API = apiUrl("");
const storedToken = () => localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
async function post(path, body, token) {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || storedToken()}`
    },
    body: JSON.stringify(body || {})
  });
  const text = await r.text();
  let d = {};
  try {
    d = text ? JSON.parse(text) : {};
  } catch (_) {
    throw new Error(r.ok ? "The server sent an unexpected reply" : `Not available (HTTP ${r.status}) — is the latest backend deployed?`);
  }
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}
const EMAIL_RX = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const splitEmails = raw => String(raw || "").split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
const dayLabel = v => v === "last" || v == null ? "the last day" : v === "first" ? "the 1st" : `day ${v}`;
const ordinal = n => `${n}${[11, 12, 13].includes(n % 100) ? "th" : {
  1: "st",
  2: "nd",
  3: "rd"
}[n % 10] || "th"}`;
const DAYS = Array.from({
  length: 31
}, (_, i) => String(i + 1));
const C = {
  card: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    border: "1px solid #D9D9D9",
    borderRadius: 8,
    background: "#FAFAFA",
    padding: "5px 10px",
    fontSize: 12.5,
    color: "#434343",
    maxWidth: 520
  },
  on: {
    fontWeight: 700,
    color: "#059669"
  },
  off: {
    fontWeight: 700,
    color: "#8C8C8C"
  },
  btn: {
    border: "1px solid #0E8CFF",
    background: "#fff",
    color: "#0E8CFF",
    borderRadius: 5,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer"
  },
  btnFill: {
    border: "1px solid #0E8CFF",
    background: "#0E8CFF",
    color: "#fff",
    borderRadius: 5,
    padding: "5px 14px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer"
  },
  btnGhost: {
    border: "1px solid #D9D9D9",
    background: "#F5F5F5",
    color: "#434343",
    borderRadius: 5,
    padding: "5px 14px",
    fontSize: 12.5,
    cursor: "pointer"
  },
  pop: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    zIndex: 3000,
    width: "min(440px, 92vw)",
    background: "#fff",
    border: "1px solid #D9D9D9",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
    padding: "12px 14px",
    textAlign: "left"
  },
  h: {
    fontSize: 13.5,
    fontWeight: 800,
    color: "#0E8CFF",
    marginBottom: 8
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "6px 0",
    fontSize: 13,
    flexWrap: "wrap"
  },
  lab: {
    fontWeight: 600
  },
  sel: {
    fontSize: 13,
    padding: "4px 8px",
    border: "1px solid #D9D9D9",
    borderRadius: 4
  },
  help: {
    fontSize: 11.5,
    color: "#5D5D5D",
    lineHeight: 1.45,
    margin: "4px 0 8px"
  },
  err: {
    fontSize: 12,
    color: "#CF1322",
    margin: "6px 0"
  }
};

// Shared shell: load settings, summary card + drop-down form, save/cancel.
function usePanel(token, section) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      setData(await post("/api/headoffice/report-settings", {}, token));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  const save = async fields => {
    const d = await post("/api/headoffice/report-settings/save", {
      section,
      ...fields
    }, token);
    setData(d);
    return d;
  };
  return {
    data,
    error,
    save,
    load
  };
}
function useOutsideClose(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = e => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, onClose]);
  return ref;
}
function GoesTo({
  managerEmail
}) {
  const list = splitEmails(managerEmail);
  return list.length ? <span>to <b>{list.join(", ")}</b> <span style={{
      color: "#8C8C8C"
    }}>(group manager email, set in Settings)</span></span> : <span style={{
    color: "#D46B08"
  }}>No group manager email is set — add one in Settings.</span>;
}

// ---------------------------------------------------------------------------
// TIMESHEETS — send on/off, send day, payroll month end (same rules as before).
// ---------------------------------------------------------------------------
export function TimesheetEmailPanel({
  token
}) {
  const {
    data,
    error,
    save
  } = usePanel(token, "timesheets");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ts = data?.timesheets;
  const openForm = () => {
    setForm({
      send: !!ts?.send,
      day: String(ts?.day ?? "last"),
      payrollMonthEnd: String(ts?.payrollMonthEnd ?? "last")
    });
    setErr(null);
    setOpen(true);
  };
  const dirty = form && ts && (form.send !== !!ts.send || form.day !== String(ts.day ?? "last") || form.payrollMonthEnd !== String(ts.payrollMonthEnd ?? "last"));
  const close = useCallback(async () => {
    if (dirty && !(await appConfirm("Discard your changes to the timesheet email?"))) return;
    setOpen(false);
  }, [dirty]);
  const ref = useOutsideClose(open, close);
  const doSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      await save({
        send: form.send,
        day: form.day === "last" ? "last" : Number(form.day),
        payrollMonthEnd: ["last", "first"].includes(form.payrollMonthEnd) ? form.payrollMonthEnd : Number(form.payrollMonthEnd)
      });
      setOpen(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return <div ref={ref} style={C.card} aria-label="Monthly timesheet email">
      <span aria-hidden="true">✉️</span>
      <span style={{
      fontWeight: 700
    }}>Monthly timesheet email:</span>
      {error ? <span style={C.err}>{error}</span> : !ts ? <span style={{
      color: "#8C8C8C"
    }}>loading…</span> : <span>
          {ts.send ? <span style={C.on}>On</span> : <span style={C.off}>Off</span>}
          {ts.send && <> · sent on {dayLabel(ts.day)} · payroll month ends {dayLabel(ts.payrollMonthEnd)}</>}
        </span>}
      {ts && <button style={C.btn} onClick={() => open ? close() : openForm()}>{open ? "Close" : "Change"}</button>}
      {open && form && <div style={C.pop} role="dialog" aria-label="Timesheet email settings">
          <div style={C.h}>✉️ Monthly timesheet email</div>
          <label style={{
        ...C.row,
        cursor: "pointer"
      }}>
            <input type="checkbox" checked={form.send} onChange={e => setForm({
          ...form,
          send: e.target.checked
        })} />
            <span style={C.lab}>Send monthly timesheets</span>
          </label>
          <div style={C.row}>
            <label style={C.lab} htmlFor="ts-payroll-end">Payroll month end:</label>
            <select id="ts-payroll-end" style={C.sel} value={form.payrollMonthEnd} onChange={e => setForm({
          ...form,
          payrollMonthEnd: e.target.value
        })}>
              <option value="last">Last day of the month</option>
              <option value="first">First day of the month</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={C.row}>
            <label style={C.lab} htmlFor="ts-send-day">Send on this day of the month:</label>
            <select id="ts-send-day" style={C.sel} value={form.day} onChange={e => setForm({
          ...form,
          day: e.target.value
        })}>
              <option value="last">Last day of the month</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={C.help}>
            Each timesheet covers the payroll month ending on the payroll month end date. Days between
            the send day and the payroll month end are filled with the staff member's scheduled hours,
            shown in italics on amber as ESTIMATED; the next month's spreadsheet opens with a
            reconciliation page of the actual clockings for those days. Days 29–31 send on the last
            day in shorter months.
          </div>
          <div style={{
        ...C.help,
        margin: "0 0 10px"
      }}>Sent <GoesTo managerEmail={data.managerEmail} /></div>
          {err && <div style={C.err}>{err}</div>}
          <div style={{
        display: "flex",
        gap: 8
      }}>
            <button style={{
          ...C.btnFill,
          opacity: !dirty || busy ? 0.55 : 1
        }} disabled={!dirty || busy} onClick={doSave}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button style={C.btnGhost} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>}
    </div>;
}

// ---------------------------------------------------------------------------
// REMOVED / EXPIRED DRUGS — send on/off, all branch managers, copy-to address.
// ---------------------------------------------------------------------------
export function ExpiredEmailPanel({
  token
}) {
  const {
    data,
    error,
    save
  } = usePanel(token, "expired");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ex = data?.expired;
  const openForm = () => {
    setForm({
      send: !!ex?.send,
      toBranchManagers: !!ex?.toBranchManagers,
      copyTo: ex?.copyTo || ""
    });
    setErr(null);
    setOpen(true);
  };
  const dirty = form && ex && (form.send !== !!ex.send || form.toBranchManagers !== !!ex.toBranchManagers || form.copyTo.trim() !== (ex.copyTo || ""));
  const close = useCallback(async () => {
    if (dirty && !(await appConfirm("Discard your changes to the removed/expired drugs email?"))) return;
    setOpen(false);
  }, [dirty]);
  const ref = useOutsideClose(open, close);
  const bad = form ? splitEmails(form.copyTo).filter(x => !EMAIL_RX.test(x)) : [];
  const noRecipient = form && form.send && !splitEmails(data?.managerEmail).length && !form.toBranchManagers && !splitEmails(form.copyTo).length;
  const doSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      await save({
        send: form.send,
        toBranchManagers: form.toBranchManagers,
        copyTo: form.copyTo
      });
      setOpen(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const who = ex ? [splitEmails(data.managerEmail).length ? "group manager" : null, ex.toBranchManagers ? "all branch managers" : null, splitEmails(ex.copyTo).length ? `copy to ${splitEmails(ex.copyTo).join(", ")}` : null].filter(Boolean) : [];
  return <div ref={ref} style={C.card} aria-label="Monthly removed/expired drugs email">
      <span aria-hidden="true">✉️</span>
      <span style={{
      fontWeight: 700
    }}>Monthly removed/expired drugs email:</span>
      {error ? <span style={C.err}>{error}</span> : !ex ? <span style={{
      color: "#8C8C8C"
    }}>loading…</span> : <span>
          {ex.send ? <span style={C.on}>On</span> : <span style={C.off}>Off</span>}
          {ex.send && <> · {who.length ? who.join(" + ") : <span style={{
          color: "#CF1322"
        }}>no recipients</span>}</>}
        </span>}
      {ex && <button style={C.btn} onClick={() => open ? close() : openForm()}>{open ? "Close" : "Change"}</button>}
      {open && form && <div style={C.pop} role="dialog" aria-label="Removed/expired drugs email settings">
          <div style={C.h}>✉️ Monthly removed/expired drugs email</div>
          <label style={{
        ...C.row,
        cursor: "pointer"
      }}>
            <input type="checkbox" checked={form.send} onChange={e => setForm({
          ...form,
          send: e.target.checked
        })} />
            <span style={C.lab}>Send monthly removed/expired drugs report</span>
          </label>
          <div style={C.help}>
            Sent on the {ordinal(ex.day)} of each month: a workbook of the drugs removed this
            year, with a summary per pharmacy per month and a tab for each pharmacy. Goes{" "}
            <GoesTo managerEmail={data.managerEmail} />.
          </div>
          <label style={{
        ...C.row,
        cursor: "pointer"
      }}>
            <input type="checkbox" checked={form.toBranchManagers} disabled={!form.send} onChange={e => setForm({
          ...form,
          toBranchManagers: e.target.checked
        })} />
            <span style={C.lab}>Email to all branch managers</span>
          </label>
          <div style={{
        ...C.help,
        marginLeft: 24
      }}>
            Each branch's managers (staff with the Manager role and an email address) get the report for
            their own branch only.
          </div>
          <div style={C.row}>
            <label style={C.lab} htmlFor="ex-copy-to">Send copy to:</label>
            <input id="ex-copy-to" type="text" value={form.copyTo} disabled={!form.send} onChange={e => setForm({
          ...form,
          copyTo: e.target.value
        })} placeholder="e.g. finance@group.co.uk" style={{
          ...C.sel,
          flex: 1,
          minWidth: 200,
          borderColor: bad.length ? "#CF1322" : "#D9D9D9"
        }} />
          </div>
          <div style={{
        ...C.help,
        marginTop: 0
      }}>
            {bad.length ? <span style={{
          color: "#CF1322"
        }}>Not a valid email address: {bad.join(", ")}</span> : "Gets the whole group's report. Separate several addresses with commas."}
          </div>
          {noRecipient && <div style={C.err}>Nobody would receive this — tick branch managers or add an address.</div>}
          {err && <div style={C.err}>{err}</div>}
          <div style={{
        display: "flex",
        gap: 8
      }}>
            <button style={{
          ...C.btnFill,
          opacity: !dirty || busy || bad.length ? 0.55 : 1
        }} disabled={!dirty || busy || bad.length > 0} onClick={doSave}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button style={C.btnGhost} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>}
    </div>;
}

// ---------------------------------------------------------------------------
// EXPORT PIN — Settings → Organisation Settings. Every staff / pay spreadsheet
// (timesheets, clock exports, payment schedules) is encrypted with this PIN.
// Until it is changed the default, 12345, is used — and this panel says so.
//   POST /api/headoffice/export-pin        → { pin, isDefault, updatedAt, updatedBy }
//   POST /api/headoffice/export-pin/save   { pin }   (4–8 digits)
// ---------------------------------------------------------------------------
export function ExportPinPanel({
  token
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const load = useCallback(async () => {
    try {
      setData(await post("/api/headoffice/export-pin", {}, token));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  const valid = /^\d{4,8}$/.test(pin);
  const match = pin === pin2;
  const save = async () => {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      const d = await post("/api/headoffice/export-pin/save", {
        pin
      }, token);
      setData(x => ({
        ...(x || {}),
        ...d
      }));
      setPin("");
      setPin2("");
      setNote(d.isDefault ? "Saved — but that is still the default PIN." : "PIN changed. Files from now on use the new PIN.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const box = {
    fontSize: 14,
    padding: "6px 8px",
    border: "1px solid #8C8C8C",
    borderRadius: 4,
    width: 120,
    letterSpacing: 2
  };
  return <div style={{
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    padding: 16
  }} aria-label="Export PIN">
      <div style={{
      fontWeight: 700,
      fontSize: 15,
      marginBottom: 6
    }}>🔒 Export PIN for staff &amp; pay spreadsheets</div>
      <div style={{
      fontSize: 12.5,
      color: "#5D5D5D",
      lineHeight: 1.5,
      marginBottom: 10
    }}>
        Every spreadsheet with staff hours, clockings or pay — the emailed timesheets and payment schedules, and the
        downloads from Staff clock in/out, Raw data and the Pharmacist rota — is encrypted with this PIN. Excel asks for it
        when the file is opened, so give it only to the people who need to open them.
      </div>
      {error && <div style={C.err}>{error}</div>}
      {!data ? !error && <div style={{
      fontSize: 13,
      color: "#8C8C8C"
    }}>Loading…</div> : <>
        {data.isDefault && <div role="alert" style={{
        background: "#FFF7E6",
        border: "1px solid #FFD591",
        color: "#AD4E00",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 13,
        marginBottom: 10
      }}>
            <b>Please change the PIN.</b> Files are currently protected with the default PIN <b>12345</b>, which is not secret.
          </div>}
        <div style={{
        ...C.row,
        marginBottom: 10
      }}>
          <span style={C.lab}>Current PIN:</span>
          <span style={{
          fontFamily: "monospace",
          fontSize: 15,
          letterSpacing: 2
        }}>{show ? data.pin : "•".repeat(String(data.pin || "").length)}</span>
          <button type="button" style={C.btn} onClick={() => setShow(v => !v)}>{show ? "Hide" : "Show"}</button>
          {data.updatedAt && !data.isDefault && <span style={{
          fontSize: 11.5,
          color: "#8C8C8C"
        }}>changed {new Date(data.updatedAt).toLocaleDateString("en-GB")}{data.updatedBy ? ` by ${data.updatedBy}` : ""}</span>}
        </div>
        <div style={C.row}>
          <label style={C.lab} htmlFor="xp-new">New PIN:</label>
          <input id="xp-new" type="password" inputMode="numeric" autoComplete="new-password" maxLength={8} style={box} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))} />
          <label style={C.lab} htmlFor="xp-new2">Repeat:</label>
          <input id="xp-new2" type="password" inputMode="numeric" autoComplete="new-password" maxLength={8} style={box} value={pin2} onChange={e => setPin2(e.target.value.replace(/\D/g, ""))} />
          <button type="button" style={{
          ...C.btnFill,
          opacity: valid && match && !busy ? 1 : 0.55
        }} disabled={!valid || !match || busy} onClick={save}>{busy ? "Saving…" : "Change PIN"}</button>
        </div>
        <div style={{
        ...C.help,
        color: pin && !valid ? "#CF1322" : pin2 && !match ? "#CF1322" : "#5D5D5D"
      }}>
          {pin && !valid ? "4 to 8 digits." : pin2 && !match ? "The two PINs don't match." : "4 to 8 digits. Saved straight away — it doesn't need the Save button at the bottom. Files already sent keep the PIN they were sent with."}
        </div>
        {note && <div style={{
        fontSize: 12.5,
        color: "#237804"
      }}>{note}</div>}
      </>}
    </div>;
}
